// src/lib/summaryCache.js
//
// The balance read behind the account hover card and the Add Funds panel
// (mission 005, 01b §9): one request for concurrent readers, cached 30 s per
// account (+ the network the answer came from), invalidated on money changes
// and cleared on logout — by generation, so a read that was in flight when
// the balance changed (or the user logged out) is discarded, never cached.
// A failed read is unknown (null, rendered "—"), never
// $0, and is not cached. The fetch must not prompt for a signature.

export const SUMMARY_TTL_MS = 30_000;

export function createSummaryCache({ fetch, now = () => Date.now(), ttl = SUMMARY_TTL_MS }) {
  const lines = new Map();        // account → { at, value }
  const inflight = new Map();     // account → { gen, promise }
  const gens = new Map();         // account → generation (bumped by invalidate)
  let epoch = 0;                  // bumped by clear (logout)
  const key = (a) => (a ? String(a).toLowerCase() : null);
  const genOf = (k) => `${epoch}:${gens.get(k) ?? 0}`;
  const api = {
    async get(account, { fresh = false } = {}) {
      const k = key(account);
      if (!k) return null;
      const hit = lines.get(k);
      if (!fresh && hit && now() - hit.at <= ttl) return hit.value;
      const g = genOf(k);
      const cur = inflight.get(k);
      if (cur && cur.gen === g) return cur.promise;
      const startEpoch = epoch;
      const promise = (async () => {
        let value = null;
        try { value = (await fetch(k)) ?? null; } catch { value = null; }
        if (inflight.get(k)?.promise === promise) inflight.delete(k);
        if (epoch !== startEpoch) return null;                 // logout during the read: discard
        if (genOf(k) !== g) return api.get(k);                  // invalidated during the read: re-read
        if (value) lines.set(k, { at: now(), value });
        return value;
      })();
      inflight.set(k, { gen: g, promise });
      return promise;
    },
    /** the cached answer for this account (and network, when given), or null */
    peek(account, networkId) {
      const v = lines.get(key(account))?.value ?? null;
      if (!v) return null;
      return networkId === undefined || v.network_id === networkId ? v : null;
    },
    invalidate(account) { const k = key(account); lines.delete(k); gens.set(k, (gens.get(k) ?? 0) + 1); },
    clear() { lines.clear(); inflight.clear(); epoch += 1; },
  };
  return api;
}
